import apiClient from './client';
import type { PaginatedResponse, ContactPreference } from '../types';
import type { InvoiceDeliveryMethod } from './billingApi';
import type {
  InstalledIntelifiDevice,
  AddIntelifiDeviceRequest,
  SwapResidentialGatewayRequest,
  SwapResidentialGatewayResponse,
} from '../types/installations';

// ==================== Types ====================

export type AccountType = 'Residential' | 'Commercial' | 'Government' | 'MDU';

export type AccountBillingMode = 'Consolidated' | 'PerLocation';

export type ContactType = 'Primary' | 'Billing' | 'Technical' | 'Emergency' | 'Accounts' | 'Other';

export interface CustomerAccount {
  id: string;
  accountNumber: string;
  status: AccountStatus;
  accountType: AccountType;
  businessName?: string;
  primaryContactId?: string;
  primaryContactName?: string;
  primaryContactEmail?: string;
  primaryContactPhone?: string;
  billingAddress?: string;
  serviceAddressId?: string;
  serviceAddress?: string;
  billingEmail?: string;
  billingPhone?: string;
  autoPayEnabled: boolean;
  paymentMethodOnFile: boolean;
  accountBalance: number;
  monthlyRecurringRevenue: number;
  addOnsMonthlyRevenue: number;
  totalMonthlyRevenue: number;
  serviceTierId?: string;
  serviceTierName?: string;
  serviceTierPrice?: number;
  serviceBundleId?: string;
  serviceBundleName?: string;
  serviceBundlePrice?: number;
  serviceBundleContractMonths?: number;
  serviceBundleEarlyTerminationFee?: number;
  serviceAreaId?: string;
  serviceAreaName?: string;
  activatedAt?: string;
  suspendedAt?: string;
  cancelledAt?: string;
  notes?: string;
  preferredContactMethod: ContactPreference;
  invoiceDeliveryMethod: InvoiceDeliveryMethod;
  externalBillingId?: string;
  customFields?: Record<string, string>;
  installationFeeOverride?: number;
  installationFeeCharged: boolean;
  effectiveInstallationFee?: number;
  // Business billing fields
  taxExempt: boolean;
  taxExemptCertificateNumber?: string;
  purchaseOrderNumber?: string;
  paymentTermsDays: number;
  // Multi-location fields
  isMultiLocation: boolean;
  accountBillingMode: AccountBillingMode;
  locationCount: number;
  contactCount: number;
  leadCount: number;
  installationCount: number;
  interactionCount: number;
  createdAt: string;
  updatedAt: string;
}

export type AccountStatus = 'Active' | 'Suspended' | 'Cancelled' | 'PendingActivation';

export interface ActivateAccountRequest {
  createFirstInvoice?: boolean;
}

export interface ActivateAccountResult {
  account: CustomerAccount;
  invoice?: {
    id: string;
    invoiceNumber: string;
    totalAmount: number;
    status: string;
  };
}

export interface AccountInteraction {
  id: string;
  accountId: string;
  type: InteractionType;
  subject: string;
  description?: string;
  createdByUserId: string;
  createdByUserName?: string;
  createdAt: string;
}

export type InteractionType = 'Call' | 'Email' | 'Chat' | 'InPerson' | 'Note' | 'ServiceRequest' | 'Complaint' | 'Other';

export interface AccountStatistics {
  totalAccounts: number;
  activeAccounts: number;
  suspendedAccounts: number;
  cancelledAccounts: number;
  pendingActivationAccounts: number;
  totalMonthlyRecurringRevenue: number;
  averageRevenuePerAccount: number;
  newAccountsThisMonth: number;
  churnedAccountsThisMonth: number;
  churnRate: number;
}

export interface AccountFilters {
  page?: number;
  pageSize?: number;
  status?: AccountStatus;
  accountType?: AccountType;
  search?: string;
}

export interface CreateAccountRequest {
  accountType?: AccountType;
  businessName?: string;
  primaryContactId?: string;
  billingAddress?: string;
  serviceAddress?: string;
  billingEmail?: string;
  billingPhone?: string;
  notes?: string;
}

export interface UpdateAccountRequest {
  accountType?: AccountType;
  businessName?: string;
  primaryContactId?: string;
  serviceTierId?: string;
  billingAddress?: string;
  serviceAddress?: string;
  billingEmail?: string;
  billingPhone?: string;
  autoPayEnabled?: boolean;
  paymentMethodOnFile?: boolean;
  preferredContactMethod?: ContactPreference;
  invoiceDeliveryMethod?: InvoiceDeliveryMethod;
  notes?: string;
  externalBillingId?: string;
  installationFeeOverride?: number;
  customFields?: Record<string, string>;
  // Business billing fields
  taxExempt?: boolean;
  taxExemptCertificateNumber?: string;
  purchaseOrderNumber?: string;
  paymentTermsDays?: number;
  // Multi-location fields
  isMultiLocation?: boolean;
  accountBillingMode?: AccountBillingMode;
}

export interface CreateInteractionRequest {
  type: InteractionType;
  subject: string;
  description?: string;
}

// ==================== Service Location Types ====================

export interface ServiceLocation {
  id: string;
  customerAccountId: string;
  addressId: string;
  addressText?: string;
  locationName: string;
  locationCode?: string;
  isPrimary: boolean;
  isActive: boolean;
  serviceTierId?: string;
  serviceTierName?: string;
  monthlyRateOverride?: number;
  effectiveMonthlyRate: number;
  billSeparately: boolean;
  billingContactId?: string;
  billingContactName?: string;
  activatedAt?: string;
  deactivatedAt?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateServiceLocationRequest {
  addressId: string;
  locationName: string;
  locationCode?: string;
  isPrimary?: boolean;
  serviceTierId?: string;
  monthlyRateOverride?: number;
  billSeparately?: boolean;
  billingContactId?: string;
  notes?: string;
}

export interface UpdateServiceLocationRequest {
  locationName?: string;
  locationCode?: string;
  isPrimary?: boolean;
  isActive?: boolean;
  serviceTierId?: string;
  monthlyRateOverride?: number;
  billSeparately?: boolean;
  billingContactId?: string;
  notes?: string;
}

// ==================== Account Contact Types ====================

export interface AccountContact {
  id: string;
  customerAccountId: string;
  contactType: ContactType;
  firstName: string;
  lastName: string;
  fullName: string;
  title?: string;
  department?: string;
  email: string;
  phone?: string;
  mobilePhone?: string;
  isPrimary: boolean;
  isActive: boolean;
  receiveInvoices: boolean;
  receiveOutageNotifications: boolean;
  receiveMarketing: boolean;
  preferredContactMethod: ContactPreference;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateAccountContactRequest {
  contactType?: ContactType;
  firstName: string;
  lastName: string;
  title?: string;
  department?: string;
  email: string;
  phone?: string;
  mobilePhone?: string;
  isPrimary?: boolean;
  receiveInvoices?: boolean;
  receiveOutageNotifications?: boolean;
  receiveMarketing?: boolean;
  preferredContactMethod?: ContactPreference;
  notes?: string;
}

export interface UpdateAccountContactRequest {
  contactType?: ContactType;
  firstName?: string;
  lastName?: string;
  title?: string;
  department?: string;
  email?: string;
  phone?: string;
  mobilePhone?: string;
  isPrimary?: boolean;
  isActive?: boolean;
  receiveInvoices?: boolean;
  receiveOutageNotifications?: boolean;
  receiveMarketing?: boolean;
  preferredContactMethod?: ContactPreference;
  notes?: string;
}

// ==================== API Functions ====================

export async function getAccounts(filters?: AccountFilters): Promise<PaginatedResponse<CustomerAccount>> {
  const response = await apiClient.get<PaginatedResponse<CustomerAccount>>('/accounts', { params: filters });
  return response.data;
}

export async function getAccount(id: string): Promise<CustomerAccount> {
  const response = await apiClient.get<CustomerAccount>(`/accounts/${id}`);
  return response.data;
}

export async function getAccountByNumber(accountNumber: string): Promise<CustomerAccount> {
  const response = await apiClient.get<CustomerAccount>(`/accounts/by-number/${accountNumber}`);
  return response.data;
}

export async function getAccountsByLead(leadId: string): Promise<CustomerAccount[]> {
  const response = await apiClient.get<CustomerAccount[]>(`/accounts/by-lead/${leadId}`);
  return response.data;
}

export async function searchAccounts(query: string, maxResults = 10): Promise<CustomerAccount[]> {
  const response = await apiClient.get<CustomerAccount[]>('/accounts/search', {
    params: { q: query, maxResults },
  });
  return response.data;
}

export async function getAccountStatistics(): Promise<AccountStatistics> {
  const response = await apiClient.get<AccountStatistics>('/accounts/statistics');
  return response.data;
}

export async function createAccount(data: CreateAccountRequest): Promise<CustomerAccount> {
  const response = await apiClient.post<CustomerAccount>('/accounts', data);
  return response.data;
}

export async function createAccountFromInstallation(installationId: string): Promise<CustomerAccount> {
  const response = await apiClient.post<CustomerAccount>(`/accounts/from-installation/${installationId}`);
  return response.data;
}

export async function updateAccount(id: string, data: UpdateAccountRequest): Promise<CustomerAccount> {
  const response = await apiClient.put<CustomerAccount>(`/accounts/${id}`, data);
  return response.data;
}

export async function activateAccount(id: string, createFirstInvoice = false): Promise<ActivateAccountResult> {
  const response = await apiClient.post<ActivateAccountResult>(`/accounts/${id}/activate`, { createFirstInvoice });
  return response.data;
}

export async function suspendAccount(id: string, reason?: string): Promise<CustomerAccount> {
  const response = await apiClient.post<CustomerAccount>(`/accounts/${id}/suspend`, { reason });
  return response.data;
}

export async function reactivateAccount(id: string): Promise<CustomerAccount> {
  const response = await apiClient.post<CustomerAccount>(`/accounts/${id}/reactivate`);
  return response.data;
}

export async function cancelAccount(id: string, reason?: string): Promise<CustomerAccount> {
  const response = await apiClient.post<CustomerAccount>(`/accounts/${id}/cancel`, { reason });
  return response.data;
}

export async function getAccountInteractions(
  accountId: string,
  page = 1,
  pageSize = 20
): Promise<PaginatedResponse<AccountInteraction>> {
  const response = await apiClient.get<PaginatedResponse<AccountInteraction>>(
    `/accounts/${accountId}/interactions`,
    { params: { page, pageSize } }
  );
  return response.data;
}

export async function addAccountInteraction(
  accountId: string,
  data: CreateInteractionRequest
): Promise<AccountInteraction> {
  const response = await apiClient.post<AccountInteraction>(`/accounts/${accountId}/interactions`, data);
  return response.data;
}

// ==================== Service Location API Functions ====================

export async function getAccountLocations(accountId: string): Promise<ServiceLocation[]> {
  const response = await apiClient.get<ServiceLocation[]>(`/accounts/${accountId}/locations`);
  return response.data;
}

export async function createAccountLocation(
  accountId: string,
  data: CreateServiceLocationRequest
): Promise<ServiceLocation> {
  const response = await apiClient.post<ServiceLocation>(`/accounts/${accountId}/locations`, data);
  return response.data;
}

export async function updateAccountLocation(
  accountId: string,
  locationId: string,
  data: UpdateServiceLocationRequest
): Promise<ServiceLocation> {
  const response = await apiClient.put<ServiceLocation>(`/accounts/${accountId}/locations/${locationId}`, data);
  return response.data;
}

export async function deleteAccountLocation(accountId: string, locationId: string): Promise<void> {
  await apiClient.delete(`/accounts/${accountId}/locations/${locationId}`);
}

// ==================== Account Contact API Functions ====================

export async function getAccountContacts(accountId: string): Promise<AccountContact[]> {
  const response = await apiClient.get<AccountContact[]>(`/accounts/${accountId}/contacts`);
  return response.data;
}

export async function createAccountContact(
  accountId: string,
  data: CreateAccountContactRequest
): Promise<AccountContact> {
  const response = await apiClient.post<AccountContact>(`/accounts/${accountId}/contacts`, data);
  return response.data;
}

export async function updateAccountContact(
  accountId: string,
  contactId: string,
  data: UpdateAccountContactRequest
): Promise<AccountContact> {
  const response = await apiClient.put<AccountContact>(`/accounts/${accountId}/contacts/${contactId}`, data);
  return response.data;
}

export async function deleteAccountContact(accountId: string, contactId: string): Promise<void> {
  await apiClient.delete(`/accounts/${accountId}/contacts/${contactId}`);
}

// ==================== Helper Functions ====================

export function getStatusLabel(status: AccountStatus): string {
  const labels: Record<AccountStatus, string> = {
    Active: 'Active',
    Suspended: 'Suspended',
    Cancelled: 'Cancelled',
    PendingActivation: 'Pending Activation',
  };
  return labels[status] || status;
}

export function getStatusVariant(status: AccountStatus): 'success' | 'warning' | 'error' | 'default' {
  const variants: Record<AccountStatus, 'success' | 'warning' | 'error' | 'default'> = {
    Active: 'success',
    Suspended: 'warning',
    Cancelled: 'error',
    PendingActivation: 'default',
  };
  return variants[status] || 'default';
}

export function getInteractionTypeLabel(type: InteractionType): string {
  const labels: Record<InteractionType, string> = {
    Call: 'Phone Call',
    Email: 'Email',
    Chat: 'Chat',
    InPerson: 'In Person',
    Note: 'Note',
    ServiceRequest: 'Service Request',
    Complaint: 'Complaint',
    Other: 'Other',
  };
  return labels[type] || type;
}

// ==================== Payment Preferences Types ====================

export interface PaymentPreferences {
  preferredPayDayOfMonth: number | null;
  nextPaymentOverrideDate: string | null;
  nextPaymentOverrideInvoiceId: string | null;
  calculatedNextPaymentDate: string | null;
  nextUnpaidInvoice: {
    id: string;
    invoiceNumber: string;
    amount: number;
    dueDate: string;
  } | null;
}

export interface UpdatePaymentPreferencesRequest {
  preferredPayDayOfMonth?: number | null;
  nextPaymentOverrideDate?: string | null;
}

// ==================== Payment Preferences API Functions ====================

export async function getPaymentPreferences(accountId: string): Promise<PaymentPreferences> {
  const response = await apiClient.get<PaymentPreferences>(`/accounts/${accountId}/payment-preferences`);
  return response.data;
}

export async function updatePaymentPreferences(
  accountId: string,
  dto: UpdatePaymentPreferencesRequest
): Promise<PaymentPreferences> {
  const response = await apiClient.patch<PaymentPreferences>(`/accounts/${accountId}/payment-preferences`, dto);
  return response.data;
}

// ==================== Import Types ====================

export interface CustomerImportRowDto {
  rowNumber: number;
  accountNumber?: string;
  accountType?: string;
  status?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  mobilePhone?: string;
  serviceAddress?: string;
  serviceCity?: string;
  serviceState?: string;
  serviceZip?: string;
  billingAddress?: string;
  billingCity?: string;
  billingState?: string;
  billingZip?: string;
  serviceTierName?: string;
  serviceAreaName?: string;
  billingEmail?: string;
  billingPhone?: string;
  autoPayEnabled?: boolean;
  preferredContactMethod?: string;
  smsOptIn?: boolean;
  externalBillingId?: string;
  equipmentSerialNumbers?: string;
  notes?: string;
  activatedAt?: string;
}

export interface CustomerImportRequest {
  rows: CustomerImportRowDto[];
  createAddresses: boolean;
  createLeads: boolean;
  skipDuplicates: boolean;
  skipMissingEquipment: boolean;
  sendWelcomeEmail: boolean;
  validateOnly: boolean;
  defaultStatus: AccountStatus;
}

export interface CustomerImportRowError {
  rowNumber: number;
  field: string;
  error: string;
}

export interface ImportedCustomerSummary {
  rowNumber: number;
  accountNumber: string;
  customerName?: string;
  serviceAddress?: string;
  addressCreated: boolean;
  addressGeocoded: boolean;
  leadCreated: boolean;
  equipmentAssigned: number;
  welcomeEmailSent: boolean;
}

export interface ImportGeocodingResult {
  rowNumber: number;
  originalAddress: string;
  formattedAddress?: string;
  latitude?: number;
  longitude?: number;
  success: boolean;
  error?: string;
  matchedServiceAreaId?: string;
  matchedServiceAreaName?: string;
}

export interface CustomerImportResult {
  success: boolean;
  totalRows: number;
  customersCreated: number;
  customersSkipped: number;
  addressesCreated: number;
  addressesMatched: number;
  addressesGeocoded: number;
  leadsCreated: number;
  equipmentAssigned: number;
  welcomeEmailsSent: number;
  welcomeEmailsFailed: number;
  errorCount: number;
  errors: CustomerImportRowError[];
  importedCustomers: ImportedCustomerSummary[];
  geocodingResults: ImportGeocodingResult[];
  message?: string;
}

export interface CustomerImportTemplateColumn {
  name: string;
  description: string;
  required: boolean;
  exampleValue?: string;
  allowedValues?: string[];
}

export interface CustomerImportTemplate {
  columns: CustomerImportTemplateColumn[];
  csvHeader: string;
  exampleRow: string;
}

// ==================== Intelifi Devices (post-install, account-scoped) ====================

export async function listAccountIntelifiDevices(
  accountId: string,
): Promise<InstalledIntelifiDevice[]> {
  const response = await apiClient.get<InstalledIntelifiDevice[]>(
    `/accounts/${accountId}/intelifi-devices`,
  );
  return response.data;
}

export async function addAccountIntelifiDevice(
  accountId: string,
  data: AddIntelifiDeviceRequest,
): Promise<InstalledIntelifiDevice[]> {
  const response = await apiClient.post<InstalledIntelifiDevice[]>(
    `/accounts/${accountId}/intelifi-devices`,
    data,
  );
  return response.data;
}

export async function removeAccountIntelifiDevice(
  accountId: string,
  deviceId: string,
): Promise<void> {
  await apiClient.delete(`/accounts/${accountId}/intelifi-devices/${deviceId}`);
}

export async function swapAccountResidentialGateway(
  accountId: string,
  data: SwapResidentialGatewayRequest,
): Promise<SwapResidentialGatewayResponse> {
  const response = await apiClient.post<SwapResidentialGatewayResponse>(
    `/accounts/${accountId}/intelifi-devices/swap-rgw`,
    data,
  );
  return response.data;
}

// ==================== Import API Functions ====================

export async function getCustomerImportTemplate(): Promise<CustomerImportTemplate> {
  const response = await apiClient.get<CustomerImportTemplate>('/accounts/import/template');
  return response.data;
}

export async function downloadCustomerImportTemplate(): Promise<void> {
  const response = await apiClient.get('/accounts/import/template/download', {
    responseType: 'blob',
  });
  const blob = new Blob([response.data], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'customer_import_template.csv';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

export async function importCustomers(request: CustomerImportRequest): Promise<CustomerImportResult> {
  const response = await apiClient.post<CustomerImportResult>('/accounts/import', request);
  return response.data;
}

// CSV Parser for customer import
export function parseCustomerCSV(csvContent: string): CustomerImportRowDto[] {
  const lines = csvContent.split('\n').filter(line => line.trim());
  if (lines.length < 2) return [];

  const headers = parseCSVLine(lines[0]);
  const rows: CustomerImportRowDto[] = [];

  for (let i = 1; i < lines.length; i++) {
    const values = parseCSVLine(lines[i]);
    const row: CustomerImportRowDto = { rowNumber: i };

    headers.forEach((header, index) => {
      const value = values[index]?.trim();
      if (value) {
        const key = header.trim().charAt(0).toLowerCase() + header.trim().slice(1);
        if (key === 'autoPayEnabled' || key === 'smsOptIn') {
          (row as unknown as Record<string, unknown>)[key] = value.toLowerCase() === 'true';
        } else {
          (row as unknown as Record<string, unknown>)[key] = value;
        }
      }
    });

    // Only add rows that have some data
    if (row.firstName || row.lastName || row.email || row.serviceAddress) {
      rows.push(row);
    }
  }

  return rows;
}

function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];

    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      result.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  result.push(current);

  return result;
}
